import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Pencil, Trash2, Star, ShoppingCart, Grid2x2, List, X, Download, ChevronDown } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { exportToExcel } from '@/lib/export-utils';
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog';
import toast from 'react-hot-toast';
import type { Artist } from '@/types';

const AdminArtworksPage: React.FC = () => {
  const artworks = useAdminStore((s) => s.artworks);
  const deleteArtwork = useAdminStore((s) => s.deleteArtwork);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'grid' | 'table'>('grid');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [catFilter, setCatFilter] = useState('All');

  const categories = useMemo(() => {
    const cats = new Set(artworks.map((a) => a.category));
    return ['All', ...Array.from(cats)];
  }, [artworks]);

  const filtered = useMemo(() => {
    let items = [...artworks];
    if (catFilter !== 'All') items = items.filter((a) => a.category === catFilter);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((a) => a.title.en.toLowerCase().includes(q) || a.title.ka.toLowerCase().includes(q));
    }
    return items;
  }, [artworks, catFilter, search]);

  const handleDelete = (): void => {
    if (!deleteId) return;
    deleteArtwork(deleteId);
    toast.success('Artwork deleted');
    setDeleteId(null);
  };

  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportScope, setExportScope] = useState<'filtered' | 'all' | 'for-sale' | 'not-for-sale'>('filtered');

  const getArtistName = (artist: Artist | string): string => typeof artist === 'string' ? artist : artist.name.en;

  const getExportList = (): typeof artworks => {
    if (exportScope === 'all') return artworks;
    if (exportScope === 'for-sale') return artworks.filter((a) => a.isForSale);
    if (exportScope === 'not-for-sale') return artworks.filter((a) => !a.isForSale);
    return filtered;
  };

  const handleExport = (): void => {
    const list = getExportList();
    if (!list.length) { toast.error('No artworks to export'); return; }
    const data = list.map((a) => ({
      'Title (EN)': a.title.en,
      'Title (KA)': a.title.ka,
      'Artist': getArtistName(a.artist),
      'Category': a.category,
      'Price (USD cents)': a.price.amount,
      'Price (USD)': '$' + (a.price.amount / 100).toFixed(2),
      'Rarity': a.rarity,
      'Size': `${a.dimensions.width}x${a.dimensions.height} ${a.dimensions.unit}`,
      'Size Class': a.sizeClass,
      'Edition': a.edition ? `${a.edition.current}/${a.edition.total}` : '',
      'For Sale': a.isForSale ? 'Yes' : 'No',
      'Featured': a.isFeatured ? 'Yes' : 'No',
      'Views': a.viewCount,
      'Slug': a.slug,
    }));
    exportToExcel(data, { filename: `artworks-${exportScope}-${new Date().toISOString().slice(0, 10)}`, sheetName: 'Artworks' });
    toast.success(`Exported ${list.length} artworks`);
    setShowExportMenu(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Artworks</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">{artworks.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button onClick={() => setShowExportMenu(!showExportMenu)} className="flex items-center gap-2 px-4 py-2.5 bg-black/[0.05] hover:bg-black/[0.1] text-[10px] font-bold uppercase tracking-wider rounded-xl transition-all">
              <Download size={14} /> Export <ChevronDown size={12} />
            </button>
            <AnimatePresence>
              {showExportMenu && (
                <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} className="absolute right-0 top-full mt-2 w-52 bg-white dark:bg-[#111] border border-black/10 dark:border-white/10 rounded-xl shadow-xl z-50 p-2">
                  {([['filtered', `Filtered (${filtered.length})`], ['all', `All (${artworks.length})`], ['for-sale', `For Sale (${artworks.filter((a) => a.isForSale).length})`], ['not-for-sale', `Not For Sale (${artworks.filter((a) => !a.isForSale).length})`]] as const).map(([key, label]) => (
                    <button key={key} onClick={() => setExportScope(key)} className={`w-full text-left px-3 py-2 text-[11px] font-medium rounded-lg transition-all ${exportScope === key ? 'bg-black dark:bg-white text-white dark:text-black' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}>
                      {label}
                    </button>
                  ))}
                  <button onClick={handleExport} className="w-full mt-2 py-2.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-wider rounded-lg">
                    Export {getExportList().length} Artworks
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Link to="/admin/artworks/new" className="flex items-center gap-2 px-5 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
            <Plus size={14} /> Add Artwork
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs min-w-[180px]">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search artworks..." className="w-full pl-10 pr-8 py-2.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-[12px] font-medium outline-none focus:border-black/15 dark:focus:border-white/15 transition-all placeholder:text-black/20 dark:placeholder:text-white/20" />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20"><X size={12} /></button>}
        </div>
        <div className="flex items-center gap-1 overflow-x-auto">
          {categories.map((cat) => (
            <button key={cat} onClick={() => setCatFilter(cat)} className={`px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] whitespace-nowrap rounded-lg transition-all ${catFilter === cat ? 'bg-black dark:bg-white text-white dark:text-black' : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'}`}>{cat}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center bg-black/[0.03] dark:bg-white/[0.04] rounded-lg p-0.5">
          <button onClick={() => setView('grid')} className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${view === 'grid' ? 'bg-black dark:bg-white text-white dark:text-black' : 'text-black/30 dark:text-white/30'}`}><Grid2x2 size={12} /></button>
          <button onClick={() => setView('table')} className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${view === 'table' ? 'bg-black dark:bg-white text-white dark:text-black' : 'text-black/30 dark:text-white/30'}`}><List size={12} /></button>
        </div>
      </div>

      {view === 'grid' && (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((aw) => (
              <motion.div key={aw._id} layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="group bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden hover:border-black/10 dark:hover:border-white/10 transition-all">
                <div className="relative aspect-[4/3] bg-black/[0.02] dark:bg-white/[0.02] overflow-hidden">
                  <img src={aw.images?.[0] || ''} alt={aw.title.en} className="w-full h-full object-cover" />
                  <div className="absolute top-2 right-2 flex gap-1">
                    {aw.isFeatured && <span className="w-6 h-6 rounded-lg bg-yellow-500/90 flex items-center justify-center"><Star size={10} className="text-white" /></span>}
                    {aw.isForSale && <span className="w-6 h-6 rounded-lg bg-green-500/90 flex items-center justify-center"><ShoppingCart size={10} className="text-white" /></span>}
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <Link to={`/admin/artworks/${aw._id}`} className="w-9 h-9 rounded-xl bg-white flex items-center justify-center hover:scale-105 transition-transform"><Pencil size={14} className="text-black" /></Link>
                    <button onClick={() => setDeleteId(aw._id)} className="w-9 h-9 rounded-xl bg-red-500 flex items-center justify-center hover:scale-105 transition-transform"><Trash2 size={14} className="text-white" /></button>
                  </div>
                </div>
                <div className="p-3.5">
                  <p className="text-[12px] font-bold truncate">{aw.title.en}</p>
                  <p className="text-[10px] text-black/30 dark:text-white/30 mt-0.5">{getArtistName(aw.artist)}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] font-bold">${(aw.price.amount / 100).toLocaleString()}</span>
                    <span className="text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/[0.04] dark:bg-white/[0.06] text-black/40 dark:text-white/40">{aw.rarity}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {view === 'table' && (
        <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead><tr className="border-b border-black/[0.04] dark:border-white/[0.04]">
              {['', 'Title', 'Artist', 'Category', 'Price', 'Rarity', 'Status', ''].map((h, i) => (
                <th key={i} className="text-left px-4 py-3 text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.map((aw) => (
                <tr key={aw._id} className="border-b border-black/[0.02] dark:border-white/[0.02] hover:bg-black/[0.01] dark:hover:bg-white/[0.01] transition-colors">
                  <td className="px-4 py-2.5"><img src={aw.images?.[0] || ''} alt="" className="w-10 h-10 rounded-lg object-cover" /></td>
                  <td className="px-4 py-2.5 text-[12px] font-bold">{aw.title.en}</td>
                  <td className="px-4 py-2.5 text-[11px] text-black/50 dark:text-white/50">{getArtistName(aw.artist)}</td>
                  <td className="px-4 py-2.5 text-[10px] text-black/40 dark:text-white/40">{aw.category}</td>
                  <td className="px-4 py-2.5 text-[11px] font-bold">${(aw.price.amount / 100).toLocaleString()}</td>
                  <td className="px-4 py-2.5"><span className="text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/[0.04] dark:bg-white/[0.06] text-black/40 dark:text-white/40">{aw.rarity}</span></td>
                  <td className="px-4 py-2.5"><div className="flex gap-1">{aw.isFeatured && <span className="w-2 h-2 rounded-full bg-yellow-500" />}{aw.isForSale && <span className="w-2 h-2 rounded-full bg-green-500" />}</div></td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      <Link to={`/admin/artworks/${aw._id}`} className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-all"><Pencil size={12} /></Link>
                      <button onClick={() => setDeleteId(aw._id)} className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-red-500 hover:bg-red-500/5 transition-all"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length === 0 && <div className="text-center py-16"><p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">No artworks found</p></div>}

      <AdminConfirmDialog isOpen={deleteId !== null} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Delete Artwork" message="This will permanently remove this artwork. This action cannot be undone." />
    </div>
  );
};

export default AdminArtworksPage;
